import { isoDate } from '../../lib/jalali'
import { faDigits } from '../../lib/numerals'
import { formatDayCompact } from './dates'

// ─── Report range helpers (S3-p) ──────────────────────────────────────────────
//
// The «گزارش» tab's inputs take a Gregorian `YYYY/MM/DD` (Persian or Latin
// digits, `/` or `-`) and the wire gets `YYYY-MM-DD`. They took Jalali until
// the panel went Gregorian (2026-10-13). Pure functions, so the crossing is
// unit-tested without a render.

const DIGIT_MAP = { '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4', '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
  '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4', '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9' }

/** Persian or Arabic-Indic digits → Latin, everything else untouched. */
export function latinDigits(text) {
  return String(text ?? '').replace(/[۰-۹٠-٩]/g, ch => DIGIT_MAP[ch])
}

/**
 * The years an admin can mean. A Jalali year typed out of habit (`1405/07/21`)
 * would otherwise be accepted as a day six centuries back and sent as such.
 */
export const INPUT_YEAR_MIN = 2000
export const INPUT_YEAR_MAX = 2100

/** A typed `YYYY/MM/DD` (any digits, `/` or `-`) → `YYYY-MM-DD`, or null when it is not a real day in range. */
export function dateInputToIso(text) {
  const m = /^\s*(\d{4})[/-](\d{1,2})[/-](\d{1,2})\s*$/.exec(latinDigits(text))
  if (!m) return null
  const [year, month, day] = m.slice(1).map(Number)
  if (year < INPUT_YEAR_MIN || year > INPUT_YEAR_MAX) return null
  const d = new Date(Date.UTC(year, month - 1, day))
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null
  return isoDate({ year, month, day })
}

/** `YYYY-MM-DD` → what the input shows for it (`۲۰۲۶/۰۹/۱۸`), or '' for nothing. */
export function isoToDateInput(ymd) {
  const text = formatDayCompact(ymd)
  return text === '—' ? '' : text
}

function localIso(d) {
  return isoDate({ year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() })
}

/** The last 30 days ending today (inclusive), as the wire's `{ from, to }` — the backend's own default, made explicit so the inputs can show it. */
export function defaultRange(today = new Date()) {
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29)
  return { from: localIso(from), to: localIso(today) }
}

/** 0–1 → `۳٫۹٪`; null (no attempts) → `—`. */
export function percentFa(rate) {
  if (rate == null) return '—'
  return `${faDigits((rate * 100).toFixed(1)).replace('.', '٫')}٪`
}

/** A count or score in Persian digits with the Persian decimal separator; null → `—`. */
export function numberFa(value) {
  if (value == null) return '—'
  return faDigits(value).replace('.', '٫')
}
